/** Gerencia os inimigos: update, separação (não empilhar), remoção e lista de alvos. */
import * as THREE from "three";
import type { Damageable } from "../combat/Damageable";
import type { TelegraphSystem } from "../fx/Telegraph";
import type { ParticleSystem } from "../fx/Particles";
import type { Sanctuary } from "../world/Sanctuary";
import type { Enemy, FireBoltFn } from "./Enemy";

export class EnemyManager {
  readonly enemies: Enemy[] = [];
  private readonly tmp = new THREE.Vector3();
  private fx: { telegraph: TelegraphSystem; particles: ParticleSystem } | null = null;
  private sanctuary: Sanctuary | null = null;
  private boltFn: FireBoltFn | null = null;

  constructor(private readonly scene: THREE.Scene) {}

  /** Fornece os sistemas de FX que os Elites usam (Ground Smash). */
  setFx(telegraph: TelegraphSystem, particles: ParticleSystem): void {
    this.fx = { telegraph, particles };
  }

  /** Função de disparo para behaviors ranged (Aetherwing / Shard Warden). */
  setBoltFn(fn: FireBoltFn): void {
    this.boltFn = fn;
  }

  /** Base segura: nenhum inimigo entra (empurrados para fora todo frame). */
  setSanctuary(sanctuary: Sanctuary): void {
    this.sanctuary = sanctuary;
  }

  add(enemy: Enemy): void {
    this.enemies.push(enemy);
    if (this.fx) enemy.attachFx(this.fx.telegraph, this.fx.particles);
    if (this.boltFn) enemy.setBoltFn(this.boltFn);
    this.scene.add(enemy.group);
  }

  get aliveCount(): number {
    let n = 0;
    for (const e of this.enemies) if (!e.isDead) n++;
    return n;
  }

  get count(): number {
    return this.enemies.length;
  }

  /** 10.9CM — `targetFor` escolhe o alvo de CADA inimigo (servo-isca próximo, senão o player). */
  update(dt: number, player: Damageable, playerSafe = false, targetFor?: (e: Enemy) => Damageable): void {
    for (const e of this.enemies) e.update(dt, targetFor ? targetFor(e) : player, playerSafe);
    this.separate();
    if (this.sanctuary) {
      for (const e of this.enemies) if (!e.isDead) this.sanctuary.keepOut(e.position, e.radius);
    }
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];
      if (e.removeRequested) {
        this.scene.remove(e.group);
        e.dispose();
        this.enemies.splice(i, 1);
      }
    }
  }

  /** 10.9HY — buckets da grade espacial da separação (reaproveitados entre quadros). */
  private readonly grid = new Map<number, Enemy[]>();
  /**
   * Empurra inimigos sobrepostos — separação mínima.
   *
   * 10.9HY — era O(n²): todo par testado todo quadro. Com o teto de 260 vivos isso dava
   * 260×259/2 = 33.670 testes POR QUADRO (~2 milhões por segundo), o maior custo de CPU do jogo
   * num celular fraco. Agora usa uma GRADE ESPACIAL: só compara quem está na mesma célula ou na
   * vizinha, que é onde a separação pode acontecer (o alcance é a soma dos raios, ~2 m). O
   * resultado é o mesmo; o custo vira ~linear.
   */
  private separate(): void {
    const list = this.enemies;
    // 10.9HZ — limpa ANTES do early-return: senão a grade segurava referências de inimigos já
    // descartados quando o mundo esvazia (clear/troca de região), impedindo a coleta.
    this.grid.clear();
    if (list.length < 2) return;
    const CELL = 4; // metros — maior que qualquer (raio a + raio b) × 1.1
    for (const e of list) {
      if (e.isDead || e.pulled) continue; // não brigar com o posicionamento do Hook
      const key = (Math.floor(e.position.x / CELL) << 16) ^ (Math.floor(e.position.z / CELL) & 0xffff);
      const bucket = this.grid.get(key);
      if (bucket) bucket.push(e); else this.grid.set(key, [e]);
    }
    for (const [key, bucket] of this.grid) {
      const cx = key >> 16;
      const cz = (key << 16) >> 16; // desfaz o xor de 16 bits com sinal
      for (let dz = 0; dz <= 1; dz++) {
        for (let dx = dz === 0 ? 0 : -1; dx <= 1; dx++) {
          const other = dx === 0 && dz === 0 ? bucket : this.grid.get(((cx + dx) << 16) ^ ((cz + dz) & 0xffff));
          if (!other) continue;
          const same = other === bucket;
          for (let i = 0; i < bucket.length; i++) {
            const a = bucket[i];
            for (let j = same ? i + 1 : 0; j < other.length; j++) {
              const b = other[j];
              if (a === b) continue;
              this.tmp.subVectors(b.position, a.position);
              this.tmp.y = 0;
              const d = this.tmp.length();
              const min = (a.radius + b.radius) * 1.1;
              if (d > 0.0001 && d < min) {
                const push = (min - d) * 0.5;
                this.tmp.multiplyScalar(1 / d);
                a.position.addScaledVector(this.tmp, -push);
                b.position.addScaledVector(this.tmp, push);
              }
            }
          }
        }
      }
    }
  }

  /** Alvos vivos para o player testar acertos. */
  targets(): readonly Damageable[] {
    return this.enemies;
  }

  clear(): void {
    for (const e of this.enemies) {
      this.scene.remove(e.group);
      e.dispose();
    }
    this.enemies.length = 0;
  }
}
